import { useState } from "react";
import MilestoneForm from "../../components/forms/milestoneForm";
import MilestoneTable from "../../components/tables/milestoneTable";
import MilestoneChecklist from "../../components/milestones/MilestoneChecklist";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import { useFirestore } from "../../hooks/useFirestore";
import { useChild } from "../../contexts/ChildContext";
import { exportToCSV, milestoneCSVColumns } from "../../utils/exportUtils";
import { exportSectionPDF } from "../../utils/pdfUtils";

const TABS = [
  { id: "log",       label: "My Milestones", icon: "🎯" },
  { id: "checklist", label: "NHS Checklist",  icon: "✅" },
];

const Milestones = () => {
  const { selectedChild } = useChild();
  const { items: milestoneData, addItem, updateItem, deleteItem, loading, error } =
    useFirestore("milestones", selectedChild?.id);

  const [activeTab, setActiveTab] = useState("log");

  const handleAddMilestone = async (milestone) => {
    try {
      await addItem({ ...milestone, childId: selectedChild?.id });
    } catch (err) {
      console.error("Error adding milestone:", err);
    }
  };

  const handleExport = () => {
    exportToCSV(milestoneData, `milestones-${selectedChild?.name || "export"}.csv`, milestoneCSVColumns);
  };

  const handleExportPDF = () => {
    exportSectionPDF(
      milestoneData,
      `milestones-${selectedChild?.name || "export"}.pdf`,
      milestoneCSVColumns,
      "Milestones",
      selectedChild?.name || ""
    );
  };

  return (
    <div className="card h-100" id="milestones">
      <div className="card-body">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">🎯 Milestones</h5>
          {activeTab === "log" && (
            <div className="d-flex gap-1">
              <button className="btn btn-sm btn-outline-secondary" onClick={handleExport}>⬇ CSV</button>
              <button className="btn btn-sm btn-outline-primary" onClick={handleExportPDF}>⬇ PDF</button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex",
          gap: "0.35rem",
          marginBottom: "1.25rem",
          borderBottom: "1px solid var(--border-light)",
          paddingBottom: "0.75rem",
        }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "0.3rem 0.9rem",
                borderRadius: "50px",
                border: activeTab === tab.id
                  ? "2px solid var(--primary-dark)"
                  : "1px solid var(--border-light)",
                background: activeTab === tab.id
                  ? "var(--primary-soft)"
                  : "transparent",
                color: activeTab === tab.id
                  ? "var(--primary-dark)"
                  : "var(--text-light)",
                fontWeight: activeTab === tab.id ? 700 : 400,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: My Milestones */}
        {activeTab === "log" && (
          <>
            {error && <div className="alert alert-danger">{error}</div>}
            <MilestoneForm onAdd={handleAddMilestone} />
            {loading ? (
              <LoadingState message="Loading milestones..." />
            ) : milestoneData.length > 0 ? (
              <MilestoneTable data={milestoneData} onUpdate={updateItem} onDelete={deleteItem} />
            ) : (
              <EmptyState
                icon="⭐"
                heading="No milestones yet"
                message="Capture that first smile, laugh, or step — the moments worth remembering."

              />
            )}
          </>
        )}

        {/* Tab: NHS Checklist */}
        {activeTab === "checklist" && (
          <MilestoneChecklist
            childId={selectedChild?.id}
            childDOB={selectedChild?.dateOfBirth}
          />
        )}
      </div>
    </div>
  );
};

export default Milestones;
